/** AES-256 key (32 bytes) as hex — same hash as JWT_SECRET on BFF/Core. */
export const LOGIN_AES_KEY_HEX =
  '7a24432646294a404e635266556a586e3272357538782f413f4428472b4b6250';

const GCM_IV_LENGTH_BYTES = 12;
const GCM_TAG_LENGTH_BITS = 128;

/**
 * AES-256-GCM (Web Crypto).
 * Compatible with BFF {@code AES/GCM/NoPadding} — IV (12 bytes) + ciphertext + auth tag, Base64.
 */
const AES_GCM = 'AES-GCM' as const;

const AES_GCM_PARAMS = { name: AES_GCM, length: 256 } as const;

const hexToUint8Array = (hex: string): Uint8Array<ArrayBuffer> => {
  if (hex.length % 2 !== 0) {
    throw new Error('Invalid AES key');
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
};

/** Copies view bytes into ArrayBuffer for Web Crypto BufferSource (TS 5.x strict typing). */
const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
};

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

/**
 * Query params may arrive percent-encoded (%2B, %3D) or double-encoded (%252B).
 * Raw Base64 with '+' must not be decoded with decodeURIComponent (turns '+' into space).
 */
const normalizeToBase64 = (value: string): string => {
  let current = value;
  while (current.includes('%')) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) {
        break;
      }
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
};

let cachedKeyPromise: Promise<CryptoKey> | null = null;

const getAes256GcmKey = (): Promise<CryptoKey> => {
  if (!cachedKeyPromise) {
    const keyBytes = hexToUint8Array(LOGIN_AES_KEY_HEX);
    cachedKeyPromise = crypto.subtle.importKey(
      'raw',
      toArrayBuffer(keyBytes),
      AES_GCM_PARAMS,
      false,
      ['encrypt', 'decrypt'],
    );
  }
  return cachedKeyPromise;
};

/** AES-256-GCM: random IV + ciphertext + auth tag, Base64-encoded. */
const encryptLoginCredential = async (plaintext: string): Promise<string> => {
  const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_LENGTH_BYTES));
  const key = await getAes256GcmKey();
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: AES_GCM, iv: toArrayBuffer(iv), tagLength: GCM_TAG_LENGTH_BITS },
    key,
    toArrayBuffer(encoded),
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return uint8ArrayToBase64(combined);
};

const decryptLoginCredential = async (encryptedBase64: string): Promise<string> => {
  const combined = base64ToUint8Array(normalizeToBase64(encryptedBase64));
  if (combined.length <= GCM_IV_LENGTH_BYTES) {
    throw new Error('Invalid encrypted payload');
  }

  const iv = combined.slice(0, GCM_IV_LENGTH_BYTES);
  const ciphertext = combined.slice(GCM_IV_LENGTH_BYTES);
  const key = await getAes256GcmKey();
  const decrypted = await crypto.subtle.decrypt(
    { name: AES_GCM, iv: toArrayBuffer(iv), tagLength: GCM_TAG_LENGTH_BITS },
    key,
    toArrayBuffer(ciphertext),
  );

  return new TextDecoder().decode(decrypted);
};

/** Encrypts CPF for verifyUser (AES-256-GCM + hash as user key). */
export const encryptLoginCpf = async (cpf: string): Promise<string> => {
  const cpfDigits = String(cpf ?? '').replace(/\D/g, '');
  if (!cpfDigits) {
    throw new Error('CPF is required');
  }
  return encryptLoginCredential(cpfDigits);
};

/** Encrypts user key for verifyUser (AES-256-GCM + hash). */
export const encryptLoginUserKey = async (userKey: string): Promise<string> => {
  if (!userKey) {
    throw new Error('User key is required');
  }
  return encryptLoginCredential(userKey);
};

/**
 * Encrypts arbitrary plaintext (AES-256-GCM + JWT hash).
 * Used for WebVida nome — Vida decrypts with the same JWT_SECRET.
 */
export const encryptAesGcmPayload = async (plaintext: string): Promise<string> => {
  if (!plaintext?.trim()) {
    throw new Error('Payload is required');
  }
  return encryptLoginCredential(plaintext.trim());
};

export const decryptLoginCpf = decryptLoginCredential;
export const decryptLoginUserKey = decryptLoginCredential;
export const decryptAesGcmPayload = decryptLoginCredential;
